<template>
    <CompassProgressBar
        v-if="status !== 'failed'"
        :state="progress >= 0 ? progress : 0"
        :caption="progressBarCaption"
        :type="CompassEnums.ProgressBarType.COLORED"
        :lowLimit="0"
        :highLimit="100"
        :class="{ 'progress-infinite': progress < 0 }"
    />
    <CompassPill v-else :label="progressBarCaption" icon="close" :color="CompassEnums.PillColor.RED" />
</template>

<script setup lang="ts">
import moment from 'moment';
import { computed, inject } from 'vue';
import type { Translator } from 'js/shims-vue';

const $t = inject('$t') as Translator;

const props = defineProps({
    status: {
        type: String,
        required: true,
    },
    progress: {
        type: Number,
        required: false,
        default: 0
    },
    started_at: {
        type: Object,
        required: false,
    }
});

// Ajout de la méthode d'estimation
const estimatedRemainingTime = computed(() => {
    if (!props.started_at || !props.progress || props.progress <= 0) return null;
    const now = moment();
    const started = moment(props.started_at);
    const elapsed = now.diff(started, 'seconds');
    if (elapsed <= 0) return null;
    const total = elapsed / (props.progress / 100);
    const remaining = Math.round(total - elapsed);
    if (remaining <= 0) return null;

    if (remaining < 60) {
        return $t.__('export.progress.remainingSeconds', { remaining });
    } else {
        return $t.__('export.progress.remainingMinutes', { remaining: Math.round(remaining / 60) });
    }
});

const progressBarCaption = computed(() => {
    if (props.status === 'pending') {
        return $t.__('export.progress.pending');
    }
    if (props.status === 'succeed') {
        return $t.__('export.progress.succeed');
    }
    if (props.status === 'failed') {
        return $t.__('export.progress.failed');
    }
    if (props.status === 'ongoing') {
        // Ajout estimation si progress > 0
        const estimation = estimatedRemainingTime.value;
        if (estimation) {
            return estimation;
        }
        return $t.__('export.progress.estimating');
    }
    return props.status;
});
</script>

<style scoped lang="scss">
.progress-infinite :deep(.compass-progress-bar__track--colored) {
    background: linear-gradient(90deg, #e0e0e0 25%, #b3b3b3 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: progress-infinite-bg 1.2s linear infinite;
}

@keyframes progress-infinite-bg {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}
</style>
